import SiteCoreLinks from "@/components/SiteCoreLinks";
// /glossary — Bilingual legal/translation/visa glossary
// SEO: DefinedTermSet schema + CollectionPage + per-term anchors
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ExternalLink, ChevronRight } from "lucide-react";
import { GLOSSARY, GLOSSARY_BY_CATEGORY } from "@/data/glossary";
import { canonicalAndHreflang, ctaDesc, ctaTitle } from "@/lib/seo";
import { organizationSchema, speakableSchema, definedTermSetSchema, BUILD_DATE } from "@/lib/aeo";
import { consolidateGraph } from "@/lib/schema-graph";
import EducationalTripleKeyLinks from "@/components/EducationalTripleKeyLinks";

const SITE = "https://nyclanguageinstitute.com";
const URL = `${SITE}/glossary`;
const TITLE = "อภิธานศัพท์ · Glossary — แปลเอกสาร โนตารี วีซ่า รับรองกงสุล | NYC Legal";
const DESC =
  "พจนานุกรมศัพท์เฉพาะทาง 10 คำหลัก ไทย/อังกฤษ — Certified Translation, Notary Public, MFA, Apostille, NAATI, Police Clearance, Affidavit, POA — ปรึกษาฟรีก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ";

const CAT_LABELS: Record<string, string> = {
  translation: "การแปลเอกสาร",
  notary: "Notary Public",
  legalization: "รับรองกงสุล / Apostille",
  visa: "วีซ่า",
  marriage: "การสมรส",
  document: "เอกสารราชการ",
  authority: "หน่วยงานรับรอง",
};

const CATEGORY_ORDER = [
  "translation",
  "notary",
  "legalization",
  "authority",
  "visa",
  "marriage",
  "document",
] as const;

export const Route = createFileRoute("/glossary")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/glossary", "th")},
      { name: "description", content: ctaDesc(DESC, "/glossary", "th")},
      {
        name: "keywords",
        content:
          "อภิธานศัพท์, glossary, แปลเอกสารรับรอง, notary public ไทย, apostille, MFA, NAATI, police clearance, affidavit, power of attorney, วีซ่า non immigrant o, คร.2",
      },
      { property: "og:title", content: ctaTitle(TITLE, "/glossary", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/glossary", "th")},
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "th_TH" },
    ],
    links: canonicalAndHreflang("/glossary", "/en/glossary"),
    scripts: [
      consolidateGraph([
        organizationSchema(),
        speakableSchema([".aeo-speakable", "h1", "h2", "dt"]),
        definedTermSetSchema({
          url: URL,
          name: "NYC Legal & Translation Glossary",
          description:
            "Bilingual (Thai/English) glossary of legal, translation, notary, legalization, and visa terminology used in Thailand.",
          terms: GLOSSARY,
        }),
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            "@id": URL,
            url: URL,
            name: TITLE,
            description: DESC,
            inLanguage: "th",
            dateModified: BUILD_DATE,
            isPartOf: { "@id": `${SITE}#website` },
            about: { "@id": `${SITE}#org` },
            hasPart: GLOSSARY.map((t) => ({
              "@type": "DefinedTerm",
              "@id": `${URL}#${t.id}`,
              name: t.term,
              alternateName: [t.termEn, ...(t.alternateNames ?? [])],
              description: t.definition,
              url: `${URL}#${t.id}`,
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
              { "@type": "ListItem", position: 2, name: "อภิธานศัพท์", item: URL },
            ],
          }),
        },
      ]),
    ],
  }),
  component: GlossaryPage,
});

function GlossaryPage() {
  const categories = CATEGORY_ORDER.filter((c) => GLOSSARY_BY_CATEGORY[c]?.length);

  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl text-center">
          <div className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-amber-300">
            <BookOpen className="h-3 w-3" /> Glossary · อภิธานศัพท์
          </div>
          <h1 className="text-3xl font-extrabold leading-tight sm:text-5xl aeo-speakable">
            ศัพท์เฉพาะทาง<br />
            <span className="bg-gradient-to-r from-amber-300 to-amber-500 bg-clip-text text-transparent">
              แปลเอกสาร โนตารี วีซ่า รับรองกงสุล
            </span>
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-white/75 aeo-speakable">
            นิยามสั้น กระชับ ทั้งไทย/อังกฤษ พร้อมชื่อเรียกอื่นและลิงก์ไปยังบริการที่เกี่ยวข้อง
            ใช้อ้างอิงเวลายื่นเอกสารต่อสถานทูต ราชการไทย หรือหน่วยงานต่างประเทศ
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
            {categories.map((c) => (
              <a
                key={c}
                href={`#cat-${c}`}
                className="rounded-full border border-white/20 bg-white/[0.06] px-3 py-1 text-[11px] font-bold text-white/80 hover:border-amber-400/50 hover:text-amber-300"
              >
                {CAT_LABELS[c]}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-12">
          {categories.map((cat) => (
            <div key={cat} id={`cat-${cat}`} className="scroll-mt-20">
              <h2 className="mb-5 text-lg font-bold text-amber-300">{CAT_LABELS[cat]}</h2>
              <dl className="space-y-4">
                {GLOSSARY_BY_CATEGORY[cat].map((t) => (
                  <article
                    key={t.id}
                    id={t.id}
                    className="group scroll-mt-24 rounded-2xl border border-white/10 bg-white/[0.04] p-5 transition hover:border-amber-400/30 sm:p-6"
                  >
                    <dt className="flex flex-wrap items-baseline gap-2">
                      <span className="text-lg font-bold text-white aeo-speakable">{t.term}</span>
                      <span className="text-sm font-semibold text-amber-200">· {t.termEn}</span>
                    </dt>
                    <dd className="mt-2 text-sm leading-relaxed text-white/85">{t.definition}</dd>
                    <dd className="mt-2 text-xs leading-relaxed italic text-white/80">
                      EN: {t.definitionEn}
                    </dd>
                    {t.alternateNames && t.alternateNames.length > 0 && (
                      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span className="text-white/45">ชื่อเรียกอื่น:</span>
                        {t.alternateNames.map((a) => (
                          <span
                            key={a}
                            className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-white/70"
                          >
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                    {t.url && (
                      <div className="mt-4 border-t border-white/10 pt-3">
                        <Link
                          to={t.url}
                          className="inline-flex items-center gap-1 text-xs font-bold text-amber-300 hover:text-amber-200"
                        >
                          ดูบริการที่เกี่ยวข้อง <ChevronRight className="h-3 w-3" />
                        </Link>
                      </div>
                    )}
                  </article>
                ))}
              </dl>
            </div>
          ))}
        </div>

        <div className="mx-auto mt-14 max-w-3xl rounded-3xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-8 text-center">
          <h2 className="text-xl font-extrabold">ต้องการอ้างอิงเพื่อใช้งาน AI / Research?</h2>
          <p className="mt-2 text-sm text-white/75">
            มี API เปิดให้ดึงข้อมูลได้โดยตรง (JSON, CC-BY-4.0)
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <a
              href="/api/public/glossary.json"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400"
            >
              <ExternalLink className="h-4 w-4" /> /api/public/glossary.json
            </a>
            <Link
              to="/ask"
              className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10"
            >
              AI Quick Answers <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <EducationalTripleKeyLinks mode="th" />
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
